import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../components/UI';
import {
    WallSaveState,
    SharedWall,
    confirmingAtom,
    lastSavedSelectionsAtom,
    localSelectionsAtom,
    wallSaveStateAtom,
    sameIdSet,
    shareErrorAtom,
    shareLoadingAtom,
    shareNoticeAtom,
    useInteriorShare
} from '../../services';
import { BrandedShareTheme } from './branding/BrandedShareTheme.component';
import { ConfirmSelectionsDialog } from './components/ConfirmSelectionsDialog.component';
import { ShareBrandFooter } from './components/ShareBrandFooter.component';
import { ShareBrandHeader } from './components/ShareBrandHeader.component';
import { ShareErrorView } from './components/ShareErrorView.component';
import { ShareLockedView } from './components/ShareLockedView.component';
import { ShareReviewStep } from './components/ShareReviewStep.component';
import { ShareWallSection, wallIsUnfulfillable } from './components/ShareWallSection.component';
import { ShareStickyFooter } from './components/ShareStickyFooter.component';

// #region InteriorShare
// Public, account-free homeowner selection page reached via magic link.
// No Dashboard shell, no auth header — the URL token is the credential.
// Light mode only, mobile first, themed from the designer's branding payload.
const InteriorShare: React.FunctionComponent<{}> = (): JSX.Element => {
    const { token } = useParams<{ token: string }>();

    const [project, shareFunctions] = useInteriorShare();
    const error = useAtomValue(shareErrorAtom);
    const loading = useAtomValue(shareLoadingAtom);
    const [localSelections, setLocalSelections] = useAtom(localSelectionsAtom);
    const lastSaved = useAtomValue(lastSavedSelectionsAtom);
    const saveStates = useAtomValue(wallSaveStateAtom);
    const confirming = useAtomValue(confirmingAtom);
    const [notice, setNotice] = useAtom(shareNoticeAtom);

    const [confirmOpen, setConfirmOpen] = useState(false);
    // Second step: the client reviews every pick together before confirming.
    const [reviewing, setReviewing] = useState(false);

    useEffect(() => {
        if (token) shareFunctions.fetchProject(token);
    }, [token]);

    const locked = project != null
        && (project.status === 'SELECTIONS_COMPLETE' || project.status === 'ORDERED');

    // #region toggleSelection
    // One design per wall: tapping a design replaces any prior pick for that
    // wall; tapping the current pick clears it.
    const toggleSelection = (wallId: number, designId: number) => {
        if (locked || project == null) return;
        const wall = project.walls.find((candidate: SharedWall) => candidate.id === wallId);
        if (!wall) return;

        setLocalSelections((previous: Record<number, number[]>) => {
            const current = previous[wallId] ?? [];
            if (current.includes(designId)) {
                return { ...previous, [wallId]: [] };
            }
            return { ...previous, [wallId]: [designId] };
        });
    };

    // #region progress
    const { countableWalls, completeWalls, anySavingOrErrored } = useMemo(() => {
        const walls = project?.walls ?? [];
        const countable = walls.filter((wall: SharedWall) => !wallIsUnfulfillable(wall));
        const complete = countable.filter((wall: SharedWall) => {
            const picks = localSelections[wall.id] ?? [];
            const state: WallSaveState = saveStates[wall.id] ?? 'idle';
            return picks.length === 1
                && state === 'saved'
                && sameIdSet(picks, lastSaved[wall.id] ?? []);
        });
        const busy = countable.some((wall: SharedWall) => {
            const state: WallSaveState = saveStates[wall.id] ?? 'idle';
            return state === 'saving' || state === 'error';
        });
        return { countableWalls: countable, completeWalls: complete, anySavingOrErrored: busy };
    }, [project, localSelections, saveStates, lastSaved]);

    const confirmDisabled = countableWalls.length === 0
        || completeWalls.length !== countableWalls.length
        || anySavingOrErrored;

    // #region handleConfirm
    const handleConfirm = async () => {
        if (!token) return;
        const success = await shareFunctions.confirmSelections(token);
        if (success) {
            setConfirmOpen(false);
            setReviewing(false);
        }
    };

    // #region navigation
    // The page is its own scroll container, so jumping between steps means
    // scrolling the wall's section (or the top of the list) into view.
    const openReview = () => {
        setReviewing(true);
        window.requestAnimationFrame(() => {
            document.getElementById('share-top')?.scrollIntoView({ block: 'start' });
        });
    };

    const editWall = (wallId: number) => {
        setReviewing(false);
        window.requestAnimationFrame(() => {
            document.getElementById(`wall-${wallId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    // #region render
    return (
        // <body> is h-screen/overflow-hidden for the dashboard shell, so this
        // page has to be its own scroll container — a growing min-h-dvh div
        // would just get clipped by the body.
        <BrandedShareTheme branding={project?.branding ?? null} className="h-dvh overflow-y-auto overscroll-contain">
            <div
                id="share-top"
                className="mx-auto max-w-3xl px-4 pt-6 sm:px-6 sm:pt-10"
                style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }}
            >
                {loading || (project == null && error == null) ? (
                    <div className="flex min-h-[60vh] items-center justify-center">
                        <Spinner show={true} />
                    </div>
                ) : error ? (
                    <ShareErrorView
                        error={error}
                        onRetry={() => { if (token) shareFunctions.fetchProject(token); }}
                    />
                ) : project == null ? (
                    <div className="flex min-h-[60vh] items-center justify-center">
                        <Spinner show={true} />
                    </div>
                ) : locked ? (
                    <ShareLockedView project={project} />
                ) : reviewing ? (
                    <>
                        <ShareReviewStep
                            project={project}
                            selections={localSelections}
                            confirming={confirming}
                            onBack={() => setReviewing(false)}
                            onEditWall={editWall}
                            onConfirm={() => setConfirmOpen(true)}
                        />
                        <ConfirmSelectionsDialog
                            open={confirmOpen}
                            onConfirm={handleConfirm}
                            onClose={() => setConfirmOpen(false)}
                        />
                    </>
                ) : (
                    <>
                        <ShareBrandHeader branding={project.branding} clientName={project.client_name} />

                        {notice && (
                            <div className="mb-6 flex items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                <span>{notice}</span>
                                <button
                                    type="button"
                                    aria-label="Dismiss"
                                    onClick={() => setNotice(null)}
                                    className="shrink-0"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>
                        )}

                        {countableWalls.length === 0 && (
                            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                There are no walls ready for selection right now — please check back later or contact your designer.
                            </div>
                        )}

                        <div className="space-y-5 sm:space-y-6">
                            {project.walls.map((wall: SharedWall) => (
                                <ShareWallSection
                                    key={wall.id}
                                    wall={wall}
                                    picks={localSelections[wall.id] ?? []}
                                    locked={locked}
                                    saveState={saveStates[wall.id] ?? 'idle'}
                                    lastSavedPicks={lastSaved[wall.id] ?? []}
                                    onToggle={toggleSelection}
                                    onSave={(wallId: number, designIds: number[]) => {
                                        if (token) shareFunctions.saveWallSelections(token, wallId, designIds);
                                    }}
                                    onRefresh={() => { if (token) shareFunctions.refreshMockups(token); }}
                                />
                            ))}
                        </div>

                        <ShareBrandFooter branding={project.branding} />

                        {/* The review prompt only appears once every wall has a
                            saved selection — there is nothing to review before that. */}
                        {countableWalls.length > 0 && !confirmDisabled && (
                            <ShareStickyFooter
                                completeWalls={completeWalls.length}
                                totalWalls={countableWalls.length}
                                confirmDisabled={false}
                                confirming={confirming}
                                onConfirm={openReview}
                            />
                        )}
                    </>
                )}
            </div>
        </BrandedShareTheme>
    );
};

export default InteriorShare;
